import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Printer } from 'lucide-react';
import DataTable from '../common/DataTable';
import PurchaseInvoiceForm from './PurchaseInvoiceForm';
import PurchaseInvoiceView from './PurchaseInvoiceView';
import ConfirmationModal from '../common/ConfirmationModal';
import { purchaseInvoiceService, supplierService } from '../../services';
import { useToast } from '../../context/ToastContext';
import { PurchaseInvoice } from '../../types';
import { toSafeFixed } from '../../utils/numberUtils';

const PurchaseInvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<PurchaseInvoice | null>(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, invoiceId: null as number | null, invoiceNumber: '' });
  const { showToast } = useToast();


  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const response = await purchaseInvoiceService.getPurchaseInvoices();
      setInvoices(response.data || response);
    } catch (error) {
      showToast('error', 'Failed to load purchase invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    showToast('success', editingInvoice ? 'Purchase invoice updated successfully' : 'Purchase invoice created successfully');
    loadInvoices();
    setEditingInvoice(null);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const handleCancel = () => {
    setEditingInvoice(null);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const handleEdit = async (invoice: PurchaseInvoice) => {
    try {
      const response = await purchaseInvoiceService.getPurchaseInvoice(invoice.id);
      setEditingInvoice(response.data || response);
      setIsFormCollapsed(false);
    } catch (error) {
      showToast('error', 'Failed to load invoice details');
    }
  };

  const handleView = (invoice: PurchaseInvoice) => {
    setViewingInvoiceId(invoice.id);
  };

  const handleDeleteClick = (invoice: PurchaseInvoice) => {
    setDeleteModal({ isOpen: true, invoiceId: invoice.id, invoiceNumber: invoice.invoice_number });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.invoiceId) return;
    
    try {
      await purchaseInvoiceService.deletePurchaseInvoice(deleteModal.invoiceId);
      setInvoices(invoices.filter(invoice => invoice.id !== deleteModal.invoiceId));
      showToast('success', 'Purchase invoice deleted successfully');
      setDeleteModal({ isOpen: false, invoiceId: null, invoiceNumber: '' });
    } catch (error) {
      showToast('error', 'Failed to delete purchase invoice');
    }
  };


  const columns = [
    {
      key: 'invoice_number',
      label: 'Invoice Number',
      sortable: true,
    },
    {
      key: 'supplier_name',
      label: 'Supplier',
      sortable: true,
    },
    {
      key: 'invoice_date',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit' 
      }),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortable: true,
      render: (value: string | undefined) => value ? new Date(value).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit' 
      }) : '-',
    },
    {
      key: 'subtotal',
      label: 'Subtotal',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'discount_amount',
      label: 'Discount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'tax_amount',
      label: 'Tax',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'total_amount',
      label: 'Total Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '1px 6px',
          fontSize: 'var(--erp-font-size-xs)',
          borderRadius: '4px',
          backgroundColor: value === 'APPROVED' ? '#dcfce7' : value === 'DRAFT' ? '#fef3c7' : value === 'PAID' ? '#dbeafe' : '#fee2e2',
          color: value === 'APPROVED' ? '#166534' : value === 'DRAFT' ? '#854d0e' : value === 'PAID' ? '#1e40af' : '#991b1b'
        }}>
          {value || 'DRAFT'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, invoice: PurchaseInvoice) => (
        <div className="flex space-x-0">
          <button
            onClick={() => handleView(invoice)}
            className={invoice.status === 'DRAFT' ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}
            title="Print"
            disabled={invoice.status === 'DRAFT'}
          >
            <Printer className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEdit(invoice)}
            className={invoice.status === 'POSTED' ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}
            title="Edit"
            disabled={invoice.status === 'POSTED'}
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteClick(invoice)}
            className={invoice.status === 'POSTED' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}
            title="Delete"
            disabled={invoice.status === 'POSTED'}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (viewingInvoiceId !== null) {
    return (
      <PurchaseInvoiceView 
        invoiceId={viewingInvoiceId} 
        onBack={() => {
          setViewingInvoiceId(null);
          loadInvoices();
        }} 
      />
    );
  }

  return (
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <PurchaseInvoiceForm
        onSave={handleSave}
        onCancel={handleCancel}
        isCollapsed={isFormCollapsed}
        onToggleCollapse={() => setIsFormCollapsed(!isFormCollapsed)}
        resetForm={resetForm}
        editData={editingInvoice}
      />

      <DataTable
        title="Purchase Invoices"
        data={invoices}
        columns={columns}
        loading={loading}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onCancel={() => setDeleteModal({ isOpen: false, invoiceId: null, invoiceNumber: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Purchase Invoice"
        message={`Are you sure you want to delete purchase invoice ${deleteModal.invoiceNumber}? This action cannot be undone.`}
      />
    </div>
  );
};

export default PurchaseInvoiceManagement;
